import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Divider,
  IconButton,
  InputAdornment,
  CircularProgress,
  Stack,
  useTheme,
} from "@mui/material";
import EditLocationIcon from "@mui/icons-material/EditLocation";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getCart, deleteCart, editCart, getCartCounter } from "../redux/slices/cart/thunk";
import LoadingComponent from "../components/LoadingComponent";
import { addOrder } from "../redux/slices/order/thunk";
import { Navigate } from "react-router-dom";

const CartPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const user = JSON.parse(localStorage.getItem("user"));
  console.log("user",user);

  // Get cart data from Redux store
  const { carts, loading, error } = useSelector((state) => state.cartSlice);
  const families = carts?.families || [];

  // Address state
  const [location, setLocation] = useState(user?.location || "");
  const [phone, setPhone] = useState(user?.phoneNumber || "");
  const [address, setAddress] = useState(user?.address || "");
  const [openAddrDialog, setOpenAddrDialog] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [newLocation, setNewLocation] = useState("");
  const [openCheckoutDialog, setOpenCheckoutDialog] = useState(false);
  const [addOrderLoading, setAddOrderLoading] = useState(false);

  // State for editing quantities
  const [editingItem, setEditingItem] = useState(null);
  const [tempQuantity, setTempQuantity] = useState(1);

  // Fetch cart data on component mount
  useEffect(() => {
    dispatch(getCart({ id: user?._id }));
    return ()=>{
      dispatch(getCartCounter({ id: user?._id }));
    }
  }, [dispatch]);

  // Compute subtotals per family and overall total
  const { familySubtotals, overallTotal } = useMemo(() => {
    if (!families || families.length === 0) {
      return { familySubtotals: [], overallTotal: 0 };
    }

    const fs = families.map((family) => {
      return family.totalPrice;
    });

    const overall = fs.reduce((a, b) => a + b, 0);
    return { familySubtotals: fs, overallTotal: overall };
  }, [families]);

  // Handler: remove an item
  const handleRemoveItem = async (cartId) => {
    await dispatch(deleteCart({ id: cartId }));
    await dispatch(getCart({ id: user?._id }));
  };

  // Handler: start editing quantity
  const handleStartEdit = (cartItem) => {
    setEditingItem(cartItem.cartId);
    setTempQuantity(cartItem.quantity);
  };

  // Handler: cancel editing
  const handleCancelEdit = () => {
    setEditingItem(null);
    setTempQuantity(1);
  };

  // Handler: save edited quantity
  const handleSaveEdit = () => {
    if (editingItem && tempQuantity > 0) {
      dispatch(
        editCart({
          id: editingItem,
          data: { quantity: tempQuantity },
        })
      );
      setEditingItem(null);
    }
  };

  // Address dialog handlers
  const handleOpenAddr = () => {
    setNewPhone(phone);
    setNewAddress(address);
    setNewLocation(location);
    setOpenAddrDialog(true);
  };

  const handleCloseAddr = () => {
    setOpenAddrDialog(false);
  };

  const handleSaveAddr = () => {
    if (newAddress.trim()) {
      setAddress(newAddress.trim());
    }
    if (newPhone.trim()) {
      setPhone(newPhone.trim());
    }
    if (newLocation.trim()) {
      setLocation(newLocation.trim());
    }
    setOpenAddrDialog(false);
  };

  const checkout = async () => {
    // Create orderNotes string from address info
    // const orderNotes = `Phone: ${phone}\nAddress: ${address}\nLocation: ${location}`;
    if(addOrderLoading) return;
    const data = {
      userId: user?._id,
      orderNotes: "",
      phone: phone,
      address: address,
      location: location,
      // families: families.map(familyGroup => ({
      //   family: {
      //     id: familyGroup.family._id,
      //     userName: familyGroup.family.userName,
      //     email: familyGroup.family.email,
      //     type: familyGroup.family.type,
      //     phoneNumber: familyGroup.family.phoneNumber
      //   },
      //   products: familyGroup.products.map(item => ({
      //     cartId: item.cartId,
      //     product: {
      //       _id: item.product._id,
      //       name: item.product.name,
      //       price: item.product.price,
      //       discount: item.product.discount
      //     },
      //     quantity: item.quantity
      //   }))
      // }))
    };

    try {
      setAddOrderLoading(true);
      setOpenCheckoutDialog(false);
      await dispatch(addOrder({ data }));
      await dispatch(getCart({ id: user?._id }));
      await dispatch(getCartCounter({ id: user?._id }));
    } catch (error) {
      console.log("error", error);
    } finally {
      setAddOrderLoading(false);
    }
  };

  // Handler to open checkout confirmation
  const handleCheckoutClick = () => {
    setOpenCheckoutDialog(true);
  };

  
      if (user?.type !== "client") {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <Container
        maxWidth="md"
        sx={{
          py: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <LoadingComponent />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          {t("cart.errorLoading", "Error loading cart data")}
        </Typography>
        <Button
          variant="outlined"
          sx={{ mt: 2 }}
          onClick={() => dispatch(getCart({ id: user?._id }))}
        >
          {t("cart.retry", "Retry")}
        </Button>
      </Container>
    );
  }

  if (!families || families.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="h5">
          {t("cart.emptyCart", "Your cart is empty")}
        </Typography>
        <Button
          variant="contained"
          sx={{ mt: 3 }}
          onClick={() => window.history.back()}
        >
          {t("cart.continueShopping", "Continue Shopping")}
        </Button>
      </Container>
    );
  }



  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Title */}
      <Typography variant="h4" sx={{ mb: 3, textAlign: "center" }}>
        {t("cart.title", "سلة المشتريات")}
      </Typography>

      {/* Address section */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {t("cart.deliveryInfo", "معلومات الوصول")}
          </Typography>

          <Box>
            <Typography variant="caption" color="text.secondary">
              {t("cart.phone", "رقم الهاتف")}
            </Typography>
            <Typography variant="body1">{phone || "-"}</Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              {t("cart.address", "العنوان")}
            </Typography>
            <Typography variant="body1">{address || "-"}</Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              {t("cart.location", "رابط الموقع")}
            </Typography>
            <Typography
              variant="body1"
              sx={{ wordBreak: "break-all", color: "primary.main" }}
            >
              {location || "-"}
            </Typography>
          </Box>
        </Box>

        <Button
          variant="outlined"
           startIcon={<EditLocationIcon sx={{ ml: 1}} />}
          onClick={handleOpenAddr}
          sx={{ height: "fit-content", alignSelf: "flex-start" }}
        >
          {t("cart.changeAddress", "تغيير العنوان")}
        </Button>
      </Paper>

      {/* Each family group */}
      <Grid container spacing={3}>
        {families.map((familyGroup, idx) => {
          if (!familyGroup.products || familyGroup.products.length === 0)
            return null;
          const family = familyGroup.family;
          const familyName =
            family.userName || t("cart.unknownFamily", "أسرة غير معروفة");
          const familyId = family.id;
          const subtotal = familySubtotals[idx] || 0;

          return (
            <Grid item xs={12} key={familyId || idx}>
              <Paper elevation={3} sx={{ p: 2 }}>
                {/* Family header */}
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t("cart.familyProducts", { familyName })}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {/* List items */}
                {familyGroup.products.map((item) => {
                  const isEditing = editingItem === item.cartId;

                  return (
                    <Box
                      key={item.cartId}
                      sx={{ display: "flex", alignItems: "center", mb: 2 }}
                    >
                      {/* Image */}
                      <Box
                        component="img"
                        src={item.product.image}
                        alt={item.product.name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src =
                            "https://via.placeholder.com/100?text=No+Image";
                        }}
                        sx={{
                          width: 80,
                          height: 80,
                          objectFit: "cover",
                          borderRadius: 1,
                          mx: 2,
                        }}
                      />

                      {/* Details */}
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 500 }}
                        >
                          {item.product.name}
                        </Typography>

                        {isEditing ? (
                          <TextField
                            type="number"
                            value={tempQuantity}
                            onChange={(e) =>
                              setTempQuantity(Number(e.target.value))
                            }
                            InputProps={{
                              inputProps: { min: 1 },
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton onClick={handleSaveEdit}>
                                    <SaveIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton onClick={handleCancelEdit}>
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            sx={{ width: 120, mt: 1 }}
                          />
                        ) : (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              {t("cart.quantity", "Quantity")}: {item.quantity}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
                              <Typography variant="body2">
                                {t("cart.unitPrice", "Unit Price")} :{" "}
                                {item.priceAfterDiscount.toFixed(2)}{" "}
                                <span>{t("rs")}</span>
                              </Typography>
                            </Box>
                          </>
                        )}
                      </Box>

                      {/* Line total and actions */}
                      <Box sx={{ textAlign: "right" }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                        >
                          {item.itemTotal.toFixed(2)} <span>{t("rs")}</span>
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            mt: 0.5,
                            direction: "ltr",
                          }}
                        >
                          {/* {!isEditing && (
                            <IconButton 
                              aria-label={t('cart.edit', 'Edit')} 
                              onClick={() => handleStartEdit(item)}
                              size="small"
                            >
                              <EditIcon fontSize="small" sx={{ color: 'primary.main' }} />
                            </IconButton>
                          )} */}

                          <IconButton
                            aria-label={t("cart.remove", "Remove")}
                            onClick={() => handleRemoveItem(item.cartId)}
                            size="small"
                            sx={{ direction: "ltr" }}
                          >
                            <DeleteIcon
                              fontSize="small"
                              sx={{ color: "#e40000", direction: "ltr" }}
                            />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}

                {/* Subtotal per family */}
                <Divider sx={{ mt: 1, mb: 1 }} />
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", pr: 1 }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t("cart.subtotal", "Subtotal")} : {subtotal.toFixed(2)}{" "}
                    <span>{t("rs")}</span>
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Overall total */}
      <Paper elevation={4} sx={{ p: 2, mt: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="h6">{t("cart.total", "Total")}</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {overallTotal.toFixed(2)} <span>{t("rs")}</span>
          </Typography>
        </Box>
      </Paper>

      <Box>
        <Button
          fullWidth
          sx={{ my: 2 }}
               startIcon={
              addOrderLoading ? (
                <CircularProgress size={20} sx={{ ml:1}} />
              ) : (
               ""
              )
            }
            disabled={addOrderLoading}
          variant="contained"
          color="primary"
          onClick={() => handleCheckoutClick()}
        
        >
          {t("cart.proceedToCheckout", "Proceed to Checkout")}
        </Button>
      </Box>

      <Dialog
        open={openCheckoutDialog}
        onClose={() => setOpenCheckoutDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
            background: theme.palette.background.default,
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          px={2}
          pt={1}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleIcon color="primary" />
            <DialogTitle sx={{ p: 0 }}>{t("checkout.title")}</DialogTitle>
          </Stack>
          <Button
            onClick={() => setOpenCheckoutDialog(false)}
            size="small"
            variant="outlined"
            color="inherit"
            sx={{ minWidth: "auto", p: 0.5 }}
          >
            <CloseIcon />
          </Button>
        </Stack>

        <Divider sx={{ my: 1 }} />

        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("checkout.message")}
          </DialogContentText>

          <Stack spacing={2}>
            <Typography variant="subtitle1">
              {t("checkout.totalAmount")} :
              <Box component="span" fontWeight="bold" mx={1}>
                {overallTotal.toFixed(2)} {t("currency")}
              </Box>
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {t("checkout.deliveryAddress")}: {address}
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1, display: "flex", gap: 2 }}>
          <Button
            onClick={() => setOpenCheckoutDialog(false)}
            variant="outlined"
            color="inherit"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={checkout}
            variant="contained"
            color="primary"
            startIcon={
              addOrderLoading ? (
                <CircularProgress size={20} sx={{ ml: 1 }} />
              ) : (
                <SaveIcon sx={{ ml: 1 }} />
              )
            }
            disabled={addOrderLoading}
          >
            {t("checkout.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Address Dialog */}
      <Dialog
        open={openAddrDialog}
        onClose={handleCloseAddr}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {t("cart.changeAddress", "تعديل بيانات التواصل")}
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label={t("cart.newPhone", "رقم الهاتف")}
              fullWidth
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
            />

            <TextField
              label={t("cart.newAddress", "العنوان الجديد")}
              fullWidth
              multiline
              minRows={2}
              value={newAddress}
              onChange={(e) => setNewAddress(e.target.value)}
            />

            <TextField
              label={t("cart.newLocation", "رابط الموقع")}
              fullWidth
              value={newLocation}
              onChange={(e) => setNewLocation(e.target.value)}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ display: "flex", gap: 2 }}>
          <Button onClick={handleCloseAddr}>
            {t("common.cancel", "إلغاء")}
          </Button>
          <Button variant="contained" onClick={handleSaveAddr}>
            {t("common.save", "حفظ")}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CartPage;
